/**
 * WinRateDashboard — analytics sliced by source · reasons · day-of-week · vehicle.
 */
import { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import { api } from "../lib/api";

export default function WinRateDashboard() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(90);
  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/dealer/analytics/win-rate?days=${days}`);
      setD(data);
    })();
  }, [days]);
  if (!d) return <div className="text-white/60 p-6">Loading analytics…</div>;

  return (
    <div className="space-y-4" data-testid="win-rate-dashboard">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/70">Win-Rate Dashboard</div>
          <div className="text-white font-black text-2xl">{d.win_rate_pct}% win rate</div>
          <div className="text-xs text-white/60">{d.totals.sold} sold · {d.totals.lost} lost · {d.totals.bad} bad · {d.totals.open} open · over {d.window_days} days</div>
        </div>
        <select className="ssc-dealer-input text-sm" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="win-rate-days">
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 180 days</option>
          <option value={365}>Last 365 days</option>
        </select>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Sources (volume vs close rate)" testid="wr-source">
          <BarTable rows={d.by_source.map((s) => ({ label: s.label, primary: s.count, secondary: s.sold, badge: s.rate + "%" }))} primary="Leads" secondary="Sold" />
        </Panel>
        <Panel title="Sold by Day of Week" testid="wr-dow">
          <DowChart data={d.by_day_of_week} />
        </Panel>
        <Panel title="Why we won (top reasons)" testid="wr-won">
          <PillList rows={d.win_reasons} tone="emerald" />
        </Panel>
        <Panel title="Why we lost (top reasons)" testid="wr-lost">
          <PillList rows={d.loss_reasons} tone="red" />
        </Panel>
        <Panel title="Bad lead reasons" testid="wr-bad">
          <PillList rows={d.bad_reasons} tone="slate" />
        </Panel>
        <Panel title="Top sold vehicles" testid="wr-vehicles">
          <ul className="space-y-1.5 text-sm">
            {d.by_vehicle.map((v) => (
              <li key={v.label} className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                <span className="text-[var(--ssc-navy)] font-bold text-xs truncate">{v.label}</span>
                <span className="text-xs text-[var(--ssc-ink-2)] font-bold whitespace-nowrap ml-2">
                  {v.count} · ${Math.round(v.gross / 1000)}k
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <div className="rounded-2xl border border-white/15 bg-white/5 p-4" data-testid={testid}>
      <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/70 mb-2">{title}</div>
      {children}
    </div>
  );
}

function BarTable({ rows, primary, secondary }) {
  const max = Math.max(1, ...rows.map((r) => r.primary));
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.label} className="bg-white rounded-lg p-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[var(--ssc-navy)] font-bold truncate">{r.label}</span>
            <span className="text-[var(--ssc-ink-2)] font-bold ml-2 whitespace-nowrap">
              {r.primary} · {r.secondary} sold · <span className="text-emerald-700">{r.badge}</span>
            </span>
          </div>
          <div className="mt-1 h-1.5 bg-[var(--ssc-paper)] rounded-full overflow-hidden flex">
            <div className="bg-[var(--ssc-blue)]" style={{ width: `${(r.primary / max) * 100}%` }} />
          </div>
        </li>
      ))}
      {rows.length === 0 && <li className="text-xs text-white/40">No data.</li>}
    </ul>
  );
}

function DowChart({ data }) {
  const max = Math.max(1, ...Object.values(data));
  return (
    <div className="flex items-end justify-between h-32 gap-1 bg-white rounded-lg p-3">
      {Object.entries(data).map(([day, n]) => (
        <div key={day} className="flex-1 flex flex-col items-center gap-1">
          <div className="text-[9px] font-bold text-[var(--ssc-ink-2)]">{n || ""}</div>
          <div className="w-full bg-[var(--ssc-blue)]/80 rounded-t" style={{ height: `${(n / max) * 100}%`, minHeight: n ? "4px" : "0" }} />
          <div className="text-[9px] font-bold text-[var(--ssc-navy)]">{day}</div>
        </div>
      ))}
    </div>
  );
}

function PillList({ rows, tone }) {
  const toneCls = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  }[tone];
  if (rows.length === 0) return <div className="text-xs text-white/40">No data.</div>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {rows.map((r) => (
        <span key={r.label} className={`text-xs font-bold px-2.5 py-1 rounded-full border ${toneCls}`}>
          {r.label} · <strong>{r.count}</strong>
        </span>
      ))}
    </div>
  );
}
